import * as z from 'zod';
import { carDetailsSchema, vtpDetailsSchema, vtpPhotoSchema } from 'lambda/vehicle-data/types';

export const brandSchema = z.enum([
  'A', // AUDI
  'V', // VW
  'S', // CUPRA
  'C', // SKODA
  'E', // BENTLEY
  'L', // LAMBORGHINI
  'M', // MAN_TRUCKS_BUS
  'N', // VW_NUTZFAHRZEUGE
  'P', // PORSCHE
  '*', // for defaults
]);

export const marketSchema = z.enum([
  'ARE', // United Arab Emirates
  'ARG', // Argentina
  'AUS', // Australia
  'AUT', // Austria
  'BEL', // Belgium
  'BHR', // Bahrain
  'BOL', // Bolivia
  'BIH', // Bosnia and Herzegovina
  'BRA', // Brazil
  'CAN', // Canada
  'CHE', // Switzerland
  'CHL', // Chile
  'CHN', // China
  'COL', // Colombia
  'CRI', // Costa Rica
  'CYP', // Cyprus
  'CYM', // Cayman Islands
  'CUW', // Curaçao
  'DEU', // Germany
  'DNK', // Denmark
  'DOM', // Dominican Republic
  'ECU', // Ecuador
  'EGY', // Egypt
  'ESP', // Spain
  'EST', // Estonia
  'FIN', // Finland
  'FRA', // France
  'GBR', // United Kingdom
  'GLP', // Guadeloupe
  'GRC', // Greece
  'GTM', // Guatemala
  'GUF', // French Guiana
  'IRL', // Ireland
  'ITA', // Italy
  'IND', // India
  'JAM', // Jamaica
  'JOR', // Jordan
  'JPN', // Japan
  'KOR', // South Korea
  'KWT', // Kuwait
  'LBN', // Lebanon
  'LCA', // Saint Lucia
  'LUX', // Luxembourg
  'LVA', // Latvia
  'LTU', // Lithuania
  'MEX', // Mexico
  'MTQ', // Martinique
  'NLD', // Netherlands
  'NOR', // Norway
  'NZL', // New Zealand
  'OMN', // Oman
  'PAN', // Panama
  'PER', // Peru
  'POL', // Poland
  'PRY', // Paraguay
  'QAT', // Qatar
  'ROU', // Romania
  'SAU', // Saudi Arabia
  'SGP', // Singapore
  'SLV', // El Salvador
  'SRB', // Serbia
  'SVK', // Slovakia
  'SWE', // Sweden
  'SXM', // Sint Maarten
  'TTO', // Trinidad and Tobago
  'TUR', // Turkey
  'TWN', // Taiwan
  'URY', // Uruguay
  'USA', // United States
  'ZAF', // South Africa
  'DEV', // Development
  '*', // for defaults
]);

export type IBrand = z.infer<typeof brandSchema>;

export type IMarket = z.infer<typeof marketSchema>;

export const experienceSchema = z.enum(['2D', '3D']);

export type EExperience = z.infer<typeof experienceSchema>;

export const carImageModeSchema = z.enum(['demo', 'comparison']);

// The values the /product-images `mode` query parameter accepts. Omitted means "everything" (demo + comparison).
export const productImagesRequestModeSchema = carImageModeSchema.extract(['comparison']);

export type ICarImageMode = z.infer<typeof carImageModeSchema>;
export type IProductImagesRequestMode = z.infer<typeof productImagesRequestModeSchema>;

export const carImageElementSchema = z.object({
  type: z.enum(['image', 'video']),
  title: z.string(),
  source: z.string(),
  priority: z.enum(['high', 'low', 'auto']).optional(),
  mode: carImageModeSchema.default('demo').optional(),
  // Image source type: 'real' for dealer photos, 'render' for rendered images
  imageType: z.enum(['real', 'render']).optional(),
});

export const comparisonImageElementSchema = z.object({
  title: z.enum(['front', 'back', 'side']),
  source: z.string(),
});

export const carImagesSchema = z.object({
  prString: z.string().optional(),
  okapiCode: z.string().optional(),
  items: z.array(carImageElementSchema),
  comparisonImages: z.array(comparisonImageElementSchema).optional(),
});

export const sharingTypeSchema = z.enum(['remote', 'broadcast', 'broadcast-local', 'device', 'dga', 'scheduled']);

export type ICarImageElement = z.infer<typeof carImageElementSchema>;
export type ICarImages = z.infer<typeof carImagesSchema>;

// ISO 8601 timestamp for when the session is scheduled to start
// Format: YYYY-MM-DDTHH:mm:ss.sssZ (UTC) - optimal for DynamoDB queries, filtering, and sorting
const scheduledStartTimeSchema = z
  .preprocess(
    (val) => {
      // Normalize falsy sentinel values to `undefined` so .optional() removes them:
      // false, null, undefined, and empty string -> treated as "no value"
      if (val === false || val === null || val === undefined || val === '') {
        return undefined;
      }
      return val;
    },
    z
      .string()
      .regex(
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
        'Must be a valid ISO 8601 UTC timestamp (YYYY-MM-DDTHH:mm:ss.sssZ)'
      )
      .refine(
        (val) => {
          const date = new Date(val);
          return !isNaN(date.getTime()) && date.toISOString() === val;
        },
        { message: 'Must be a valid date and time in ISO 8601 UTC format' }
      )
  )
  .optional();

export const prStringSchema = z.string();
export type TPrString = z.infer<typeof prStringSchema>;

export const sessionVehicleSchema = z.object({
  prString: prStringSchema.optional(),
  // AVS-4481: the VW/VWN model revision (MP1/MP2); it travels with `prString`. '' means none. Deliberately
  // lenient (no enum), for the AVS-4477 reasons: an unexpected value must not fail the session parse.
  modelRevision: z.string().default(''),
  carImages: carImagesSchema.optional(),
  okapiCode: z.string().optional(),
  carImagesStatus: z.enum(['unset', 'loading', 'ready']).default('unset'),
  carDetails: carDetailsSchema.optional(),
  wltpText: z.string().optional(),
  carStatus: z.enum(['unset', 'loading', 'ready']).default('unset'),
  carDisplayName: z.string().optional(),
  vinOrMarketingCode: z.string().optional(),
  dgaLink: z.string().optional(),
  // Extracted from wltpData (lambda response) which is not persisted — only wltpText is.
  // Stored separately so the energy label resolver can look up the label image by class.
  efficiencyClass: z.string().optional(),
  // VTP-specific fields (only populated when resolved via VTP API)
  isVtp: z.boolean().optional(),
  vtpDetails: vtpDetailsSchema.optional(),
  vtpPhotos: z.array(vtpPhotoSchema).optional(),
});

export const vehicleSchema = sessionVehicleSchema.extend({
  // this extra field should not be copied to the session data
  addedAt: z.number().default(() => Date.now()),
  comparing: z.boolean().default(false),
});

export const vehicleIdSchema = z.string();
export type IVehicleId = z.infer<typeof vehicleIdSchema>;

export type IVehicle = z.infer<typeof vehicleSchema>;
export type ISessionVehicle = z.infer<typeof sessionVehicleSchema>;

// Full stack schema is reused in both client and server code
export const fullStackSessionSchema = sessionVehicleSchema.extend({
  id: z.string().default(() => crypto.randomUUID()),
  type: z.enum(['REMOTE', 'LOCAL']).optional(),
  tenant: z.string().optional(),
  timestamp: z.number().optional(),
  endedAt: z.number().optional(),
  partnerToken: z.string().default(''),
  brand: brandSchema.optional(),
  market: marketSchema.optional(),
  locale: z.string().default('en'),
  userPseudonym: z.string().optional(),
  dealerId: z.string().optional(),
  dealerName: z.string().default(''),
  dealerEmail: z.string().default(''),
  dealerPhone: z.string().default(''),
  dealerPhoto: z.string().default(''),
  streamActive: z.boolean().default(true),
  // When the dealer has confirmed the Waiting Room screen, then customer is ready.
  // However, if the tenant doesn't allow VoIP, the customer is automatically ready.
  customerAllowed: z.boolean().default(false),
  customerReady: z.boolean().default(false),
  // AVS-4460: written by a customer whose 3D stream hit the capacity cutoff (4 failed joins), and
  // cleared once a customer's deck connects. The dealer reads it to warn that the customer could
  // not connect to 3D. Customer-owned: the session is the only customer→dealer channel.
  customer3DUnavailable: z.boolean().default(false),
  customerAllowance: z.boolean().default(false),
  imageOverrideMode: z.enum(['auto', 'manual']).default('auto'),
  /**
   * @deprecated Use runtime.selectImage(index) instead. This field is maintained for backwards compatibility only.
   * Setting this value will trigger a console warning and automatically call selectImage() internally.
   */
  currentImage: z.number().optional(),
  // Unique identifiert for a cloud streaming session provided by team 3DWS
  // Users can connect to a shared session by providing the same aveSessionId
  aveSessionId: z.string().optional(),
  // Unique identifier for a local AVE (not session)
  // Local AVEs don't support shared sessions / multisessions, so this is only used for connecting to the correct local AVE, and verifying the connection has been established
  aveChannelName: z.string().optional(),
  // Unique per-tab client ID that initiated the local AVE connection; used to prevent other tabs from connecting to the same device
  aveClientId: z.string().optional(),
  // Explicit takeover marker (aveChannelName alone cannot distinguish a real takeover from the transient not-yet-connected state during a normal new connection)
  localAveTakenOver: z.boolean().default(false),
  // Elected single VOIP publisher PER DEALER across their tabs (AVS-4302), keyed by
  // dealerPseudonym. A MAP, not a single object: two distinct dealers each running
  // multiple tabs must elect their own master/standby independently — a global slot
  // let only one dealer own it at a time (the other dealer's tabs all published).
  // The session-sync pipeline shallow-replaces top-level keys (no deep merge), so every
  // write MUST be read-modify-write (spread the current map, never a bare singleton);
  // otherwise dealer A's sync clobbers dealer B's entry. `vehicles` (schema.ts:243) is
  // the shipped precedent for a synced top-level record travelling this same pipeline.
  // Bound the key and tabId (parity with connectionMetaSchema.dealerPseudonym) so a
  // crafted session write can't store oversized strings (DynamoDB item-size / log bloat).
  // `gen` orders claims (AVS-4302 crossed-claim race): a takeover writes holder-gen+1 (never
  // stolen back); equal gens tie-break on lower tabId. Bounded like tabId so a crafted value
  // cannot jam the +1 ordering. Optional: old persisted sessions parse as gen 0.
  // AVS-4454: `tabId` holds the per-session slot digest (`getVoipSlotId`), never the raw clientId.
  voipMasters: z
    .record(
      z.string().max(128),
      z.object({
        tabId: z.string().max(128),
        gen: z.number().int().nonnegative().max(1_000_000_000).optional(),
        // AVS-4485: a closing master names the one surviving tab that takes over with media.
        handoffTo: z.string().max(128).optional(),
      })
    )
    .default({}),
  // AVS-4485: the dealer tab that hosts the 3D stream when it is not the VOIP master (the master tab
  // is hidden while the dealer works in another tab). Gen-ordered like `voipMasters`; `away` marks a
  // hidden holder that a visible same-dealer tab may claim from. Empty: the master hosts.
  streamHosts: z
    .record(
      z.string().max(128),
      z.object({
        tabId: z.string().max(128),
        gen: z.number().int().nonnegative().max(1_000_000_000).optional(),
        away: z.boolean().optional(),
      })
    )
    .default({}),
  sharingUri: z.string().optional().default(''),
  // AVS-4477: integrator-provided Showroom config-id from `E3D.init({ showroomConfigId })`. Written only by a
  // web-component dealer; `''` means "use the brand default". Not validated (unknown ids pass through) and
  // dealer-owned: a customer only receives it (see customerSessionSchema) and never writes it back.
  showroomConfigId: z.string().default(''),
  sharingType: sharingTypeSchema.optional(),
  renderer: z.enum(['cloud', 'local', '2d']).default('cloud'),
  streamingDevice: z.string().default(''),
  // Flag indicated when the dealer starts sharing via the Sharing Dashboard
  isShared: z.boolean().default(false),
  activeExperience: experienceSchema.default('2D'),
  scheduledStartTime: scheduledStartTimeSchema.optional(),

  // Multi-vehicle support: Record/map structure for O(1) access by ID
  vehicles: z.record(vehicleIdSchema, vehicleSchema).default({}),
  activeVehicleId: vehicleIdSchema.optional(),

  // Comparison modes: off - not enabled, selecting - user is selecting vehicles to compare (gallery view), on - comparing mode active (compare view)
  comparisonMode: z.enum(['off', 'selecting', 'on']).default('off'),
});

// Internal session schema is used only on the server side
export const internalSessionSchema = fullStackSessionSchema.extend({
  // Composite key for efficient GSI queries: dealerId#userPseudonym
  dealerIdentityKey: z.string().optional(),
  // Server-set optimistic concurrency timestamp — used by ServiceAbstract.update()
  // to reject stale writes via DynamoDB ConditionExpression. Never set by client.
  updatedAt: z.number().optional(),
  // Server-set monotonic write counter, incremented atomically on every update
  // (AVS-4330). Unlike `updatedAt` (a server *clock* reading, which cannot order
  // concurrent writers), this is a real logical clock: clients gate incoming
  // broadcasts on it and may pass it back as `expectedVersion` for a conditional
  // write. Never set by client.
  version: z.number().optional(),
  // TTL in Unix epoch seconds - DynamoDB automatically deletes sessions after this time
  ttl: z.number().optional(),
});

export type IFullStackSession = z.infer<typeof fullStackSessionSchema>;
export type IInternalSession = z.infer<typeof internalSessionSchema>;
// For backward compatibility (deprecated, use IInternalSession instead)
export type ISessionData = IFullStackSession;

export const brandNames: Record<IBrand, string> = {
  A: 'Audi',
  V: 'Volkswagen',
  S: 'Cupra',
  E: 'Bentley',
  N: 'Volkswagen Nutzfahrzeuge',
  C: 'Skoda',
  L: 'Lamborghini',
  P: 'Porsche',
  M: 'MAN Trucks & Bus',
  '*': 'Default brand (fallback)',
};
